import type { TFunction } from 'i18next';
import { getApiErrorMessage } from '../utils/errorMessageUtils';
import { buildUserErrorMessage } from './userErrorService';
import {
  LocationAssignmentError,
  LocationCreatedWithFailedRightsError
} from './locationUserService';

interface LocationSaveErrorContext {
  t: TFunction;
  // The name the operator typed, so a create failure can name the location that
  // now exists rather than describing it abstractly.
  name: string;
  // False when the save was a create, which changes both the fallback wording
  // and what the operator has to do next.
  isEditing: boolean;
}

/**
 * Turns a save rejection into the sentence shown to the operator.
 *
 * Kept out of the component because it is a three-way taxonomy over error types
 * rather than anything to do with rendering, and because the distinction it
 * draws is easy to get wrong: a partial save must never read as a total
 * failure. Telling someone "could not save" when some users already hold new
 * rights, or "could not create the location" when the location exists, sends
 * them to fix the wrong thing.
 */
export const buildLocationSaveErrorMessage = (
  error: unknown,
  { t, name, isEditing }: LocationSaveErrorContext
): string => {
  let message: string;

  if (error instanceof LocationCreatedWithFailedRightsError) {
    // The location exists. Leading with that is what stops the operator
    // assuming nothing happened and creating a duplicate.
    message = t('config-loc-create-partial-error', {
      name,
      failed: error.failedUserNames.length,
      users: error.failedUserNames.join(', ')
    });
  } else if (error instanceof LocationAssignmentError) {
    message = t('config-loc-save-partial-error', {
      saved: error.savedCount,
      failed: error.failedUserNames.length,
      users: error.failedUserNames.join(', ')
    });
  } else {
    return getApiErrorMessage(
      error,
      isEditing ? t('config-loc-save-error') : t('config-loc-create-error')
    );
  }

  // Both partial cases carry the rejection that caused them, so a permanent
  // failure (a 403) reads differently from a transient one worth retrying. The
  // rejection came from the user endpoint, which answers with a code: clearing
  // someone's last role here is a ROLE_ASSIGNMENT_REQUIRED refusal, and the
  // operator needs that in their own language, not in the body's English.
  const reason = buildUserErrorMessage(error.firstFailureCause, t, '');
  return reason ? `${message} ${t('config-loc-save-reason', { reason })}` : message;
};
